/**
 * AI-tegoed — waarschuwing vóórdat OpenAI of Anthropic geen aanvragen meer aanneemt.
 *
 * Aanleiding: 2 okt 2026 was het OpenAI-tegoed op en faalden offertes, meeting-
 * samenvattingen en wiki-zoeken zonder dat iemand het wist (19 sep idem Anthropic).
 *
 * - Direct alarm: de engine herkent 'tegoed op' bij elke AI-call en mailt (max 1× per
 *   uur per provider). Daar is hier niets voor in te stellen behalve de ontvangers.
 * - Vooraf: een dagelijkse check haalt het verbruik op via de admin-key van de
 *   provider. Het saldo zelf is niet uit te lezen, dus: laatste opwaardering − kosten
 *   sindsdien. Vul na elke opwaardering bedrag + datum in.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { SectionHeader, T } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { voerUitgesteldUit } from '../../../utils/uitgesteld';

const URL_BASIS = `${BACKEND_URL}/Api/ai-tegoed`;

async function lees(r) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
  return d;
}

const dollar = (v) => (typeof v === 'number' ? `$${v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

const fmtTijd = (iso) => {
  if (!iso) return '—';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function checkLabel(p) {
  if (!p.admin_key_aanwezig) return { label: 'Admin-key ontbreekt', color: 'default' };
  const s = p.check?.status;
  if (s === 'ok') return { label: 'Ruim voldoende', color: 'success' };
  if (s === 'waarschuwing') return { label: 'Bijvullen', color: 'warning' };
  if (s === 'fout') return { label: 'Ophalen mislukt', color: 'error' };
  return { label: 'Nog niet gecontroleerd', color: 'default' };
}

function Kengetal({ label, waarde, sub }) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography sx={{ fontSize: '0.75rem', color: T.secondary }}>{label}</Typography>
      <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 700, color: T.text }}>{waarde}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: T.secondary }}>{sub}</Typography>}
    </Box>
  );
}

function ProviderKaart({ p, invoer, zetInvoer }) {
  const s = checkLabel(p);
  const alarm = p.laatste_alarm;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.05rem' }}>{p.label}</Typography>
        <Chip size="small" label={s.label} color={s.color} variant={s.color === 'default' ? 'outlined' : 'filled'} />
      </Stack>

      <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 2 }}>
        <Kengetal label="Geschat resterend" waarde={dollar(p.check?.geschat_resterend)}
          sub={p.instellingen.opwaardering_datum ? `sinds opwaardering ${p.instellingen.opwaardering_datum}` : 'vul de laatste opwaardering in'} />
        <Kengetal label="Verbruik deze maand" waarde={dollar(p.check?.kosten_maand)}
          sub={p.instellingen.maandlimiet_usd ? `limiet ${dollar(p.instellingen.maandlimiet_usd)}` : null} />
        <Kengetal label="Laatste alarm" waarde={alarm ? fmtTijd(alarm.tijd) : 'geen'}
          sub={alarm ? alarm.status : null} />
      </Stack>

      {!p.admin_key_aanwezig && (
        <Typography sx={{ fontSize: '0.8rem', color: T.secondary, mb: 1.5 }}>
          Admin-key ontbreekt ({p.admin_key_env}) — de dagelijkse check slaat {p.label} over.
          Het directe alarm bij ‘tegoed op’ werkt wel.
        </Typography>
      )}
      {p.check?.status === 'fout' && (
        <Typography sx={{ fontSize: '0.8rem', color: 'error.main', mb: 1.5 }}>{p.check.fout}</Typography>
      )}
      {(p.check?.waarschuwingen || []).map((w) => (
        <Typography key={w.soort} sx={{ fontSize: '0.8rem', color: 'warning.dark', mb: 0.5 }}>{w.tekst}</Typography>
      ))}

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
        <TextField size="small" label="Laatste opwaardering ($)" type="number" inputProps={{ min: 0, step: '0.01' }}
          value={invoer.opwaardering_bedrag ?? ''} onChange={(e) => zetInvoer('opwaardering_bedrag', e.target.value)}
          sx={{ width: 190 }} />
        <TextField size="small" label="Datum opwaardering" type="date" InputLabelProps={{ shrink: true }}
          value={invoer.opwaardering_datum ?? ''} onChange={(e) => zetInvoer('opwaardering_datum', e.target.value)}
          sx={{ width: 170 }} />
        <TextField size="small" label="Maandlimiet ($, optioneel)" type="number" inputProps={{ min: 0, step: '1' }}
          value={invoer.maandlimiet_usd ?? ''} onChange={(e) => zetInvoer('maandlimiet_usd', e.target.value)}
          sx={{ width: 200 }} />
      </Stack>
      <Typography sx={{ fontSize: '0.75rem', color: T.secondary, mt: 1 }}>
        Bijvullen: <Link href={p.bijvullen_url} target="_blank" rel="noopener noreferrer">{p.bijvullen_url.replace('https://', '')}</Link>
        {' '}— account {p.account}
      </Typography>
    </Paper>
  );
}

const leegNaarNull = (v) => (v === '' || v === undefined ? null : v);

export default function AITegoed() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [invoer, setInvoer] = useState(null);
  const [bezig, setBezig] = useState('');

  const laad = useCallback(async () => {
    try {
      const d = await lees(await authFetch(URL_BASIS));
      setData(d);
      setInvoer({
        ontvangers: d.config.ontvangers || '',
        drempel_usd: d.config.drempel_usd ?? '',
        maandlimiet_pct: d.config.maandlimiet_pct ?? '',
        providers: Object.fromEntries(d.providers.map((p) => [p.provider, { ...p.instellingen }])),
      });
      setFout(null);
    } catch (e) {
      setFout(e.message || 'Kon de status niet ophalen');
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const opslaan = async () => {
    setBezig('opslaan');
    try {
      const providers = Object.fromEntries(Object.entries(invoer.providers).map(([k, v]) => [k, {
        opwaardering_bedrag: leegNaarNull(v.opwaardering_bedrag),
        opwaardering_datum: leegNaarNull(v.opwaardering_datum),
        maandlimiet_usd: leegNaarNull(v.maandlimiet_usd),
      }]));
      await lees(await authFetch(`${URL_BASIS}/instellingen`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ontvangers: invoer.ontvangers, drempel_usd: leegNaarNull(invoer.drempel_usd),
          maandlimiet_pct: leegNaarNull(invoer.maandlimiet_pct), providers,
        }),
      }));
      // Schatting meteen bijwerken met de nieuwe opwaardering (zonder mail).
      await authFetch(`${URL_BASIS}/check?mailen=false`, { method: 'POST' }).catch(() => null);
      toast.success('Opgeslagen');
      await laad();
    } catch (e) {
      toast.error(e.message || 'Opslaan mislukt');
    } finally {
      setBezig('');
    }
  };

  const verversen = async () => {
    setBezig('check');
    try {
      await lees(await authFetch(`${URL_BASIS}/check?mailen=false`, { method: 'POST' }));
      await laad();
    } catch (e) {
      toast.error(e.message || 'Ophalen mislukt');
    } finally {
      setBezig('');
    }
  };

  const testAlarm = async () => {
    const r = await voerUitgesteldUit('Test-alarm wordt naar jou gemaild…', async () => {
      const d = await lees(await authFetch(`${URL_BASIS}/test-alarm`, { method: 'POST' }));
      return d;
    });
    if (r.geannuleerd || r.fout) return;
    const { status, naar } = r.resultaat || {};
    if (status === 'sent') toast.success(`Test-alarm verstuurd naar ${naar}`);
    else toast(`Test-alarm niet verstuurd (${status}) — op test gaat er geen mail de deur uit`, { icon: 'ℹ️' });
    await laad();
  };

  if (fout) return <Alert severity="error">{fout}</Alert>;
  if (!data || !invoer) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  const zetProvider = (provider) => (veld, waarde) => setInvoer((o) => ({
    ...o, providers: { ...o.providers, [provider]: { ...o.providers[provider], [veld]: waarde } },
  }));

  return (
    <Box>
      <SectionHeader
        title="AI-tegoed"
        subtitle={`Direct een mail als OpenAI of Anthropic ‘tegoed op’ meldt, en dagelijks een saldo-check vooraf. Laatste check: ${fmtTijd(data.laatste_check)}${data.dry_run ? ' · deze omgeving mailt niet (dry-run)' : ''}.`}
      />

      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" onClick={verversen} disabled={!!bezig}
          startIcon={bezig === 'check' ? <CircularProgress size={14} /> : null}>
          Verbruik ophalen
        </Button>
        <Button size="small" variant="text" onClick={testAlarm} disabled={!!bezig}>
          Test-alarm sturen
        </Button>
      </Stack>

      <Stack spacing={2}>
        {data.providers.map((p) => (
          <ProviderKaart key={p.provider} p={p} invoer={invoer.providers[p.provider] || {}}
            zetInvoer={zetProvider(p.provider)} />
        ))}

        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, mb: 1.5 }}>Waarschuwen</Typography>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <TextField size="small" label="Drempel resterend ($)" type="number" inputProps={{ min: 0 }}
              value={invoer.drempel_usd} onChange={(e) => setInvoer((o) => ({ ...o, drempel_usd: e.target.value }))}
              sx={{ width: 180 }} />
            <TextField size="small" label="Waarschuwen bij % maandlimiet" type="number" inputProps={{ min: 1, max: 100 }}
              value={invoer.maandlimiet_pct} onChange={(e) => setInvoer((o) => ({ ...o, maandlimiet_pct: e.target.value }))}
              sx={{ width: 220 }} />
            <TextField size="small" label="Ontvangers (komma-gescheiden)" value={invoer.ontvangers}
              onChange={(e) => setInvoer((o) => ({ ...o, ontvangers: e.target.value }))} sx={{ flex: 1, minWidth: 260 }} />
          </Stack>
        </Paper>

        <Box>
          <Button variant="contained" onClick={opslaan} disabled={!!bezig}>
            {bezig === 'opslaan' ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
